import { useState, useMemo, useEffect } from 'react';
import { FaCheck } from 'react-icons/fa';
import InputField from '../../../component/form/FormInput';
import rupayImg from '../../../assets/image/checkout/rupay-img.png';
import visaImg from '../../../assets/image/checkout/visa-img.png';
import lockImg from '../../../assets/image/checkout/lock.svg';
import lineImg from '../../../assets/image/checkout/line-img.svg';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { usePayment } from '../../../hooks/usePayment';
import { setBillingType } from '../../../store/reducers/subscriptionSlice/subscriptionSlice';
import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import useSubUrl from '../../../hooks/useSubUrl';
import { useCheckoutFormApi } from '../CheckoutFormContext';

const COUPONS = {
	SAVE50: 0.5,
	SAVE60: 0.6,
	SAVE80: 0.8,
	SAVE90: 0.9,
	SAVE100: 1.0,
};

export default function OrderSummary() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const { suburl: suburlParam } = useParams();
	const suburl = useSubUrl();
	const activeSuburl = suburlParam || suburl;

	const { selectedPlan, billingType } = useSelector((state) => state.subscription);

	const { initiatePayment, loading } = usePayment();
	const formApi = useCheckoutFormApi();

	const [coupon, setCoupon] = useState('');
	const [appliedCoupon, setAppliedCoupon] = useState(null);

	const activeBilling = useMemo(() => {
		if (!selectedPlan?.billing?.length) return null;

		return selectedPlan.billing.find((item) => item.period === billingType);
	}, [selectedPlan, billingType]);

	const planAmount = useMemo(() => {
		return Number(activeBilling?.amount_without_gst_in_rupees || 0);
	}, [activeBilling]);

	const gstAmount = useMemo(() => {
		return Number(activeBilling?.tax_amount_in_rupees || 0);
	}, [activeBilling]);

	const totalAmount = useMemo(() => {
		return Number(activeBilling?.amount_in_rupees || 0);
	}, [activeBilling]);

	const subscriptionDates = useMemo(() => {
		const today = new Date();

		const expiry = new Date(today);

		if (billingType === 'monthly') {
			expiry.setMonth(expiry.getMonth() + 1);
		} else {
			expiry.setFullYear(expiry.getFullYear() + 1);
		}

		const formatDate = (date) =>
			date.toLocaleDateString('en-IN', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
			});

		return {
			today: formatDate(today),
			expiry: formatDate(expiry),
		};
	}, [billingType]);

	const discount = useMemo(() => {
		if (!appliedCoupon) return 0;

		const percentage = COUPONS[appliedCoupon] || 0;

		return planAmount * percentage;
	}, [appliedCoupon, planAmount]);

	// Side effects (navigation) must not run during render — doing so directly
	// in the render body causes "Cannot update a component while rendering a
	// different component" warnings and unreliable navigation under React's
	// concurrent rendering. Redirect from an effect instead.
	useEffect(() => {
		if (!selectedPlan) {
			navigate(activeSuburl ? `/user/${activeSuburl}/subscription-plan` : '/subscription-plan');
		}
	}, [selectedPlan, navigate, activeSuburl]);

	if (!selectedPlan) {
		return null;
	}

	const handleApplyCoupon = () => {
		const normalized = coupon.trim().toUpperCase();

		if (COUPONS[normalized]) {
			setAppliedCoupon(normalized);
			toast.success('Coupon applied');
		} else {
			toast.error('Invalid Coupon');
		}
	};

	const handleRemoveCoupon = () => {
		setAppliedCoupon(null);
		setCoupon('');
	};

	const handlePayment = async () => {
		// Read the billing form only at click time via the shared ref, instead
		// of mirroring every keystroke into Redux.
		const { trigger, getValues } = formApi.current;
		const isFormValid = await trigger();

		if (!isFormValid) {
			return;
		}

		if (!activeBilling?.razorpay_plan_id) {
			toast.error('Invalid plan selected');
			return;
		}

		const billingData = getValues();

		try {
			await initiatePayment({
				billingDetails: billingData,
				paymentType: billingData?.paymentType,
				billingType,
				plan: selectedPlan,
				paymentMethod: billingData?.paymentMethod,
			});
		} catch (error) {
			console.error(error);
			toast.error('Payment initialization failed');
		}
	};

	const shouldShowSwitchToStarter = selectedPlan && selectedPlan?.title !== 'Starter';

	return (
		<div className="card-custom order-summary">
			<div className="w-100 d-flex justify-content-between align-items-start gap-3">
				<h5 className="card-title">Order Summary</h5>

				{shouldShowSwitchToStarter && (
					<span
						onClick={() =>
							navigate(
								`/subscription-plan?billing=${billingType}&plan=${selectedPlan?.key}&step=select-plan`,
							)
						}
						className="switch-plan"
					>
						Switch Plan
					</span>
				)}
			</div>
			<div className="w-100 mt-2">
				<div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
					<h6 className="plan-title m-0">{selectedPlan?.title}</h6>

					<div className="text-end">
						<div className="plan-price">₹ {planAmount.toFixed(2)}</div>
					</div>
				</div>

				<div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
					<span className="belling-text fw-semibold">Billing Period</span>

					<select
						className="form-select billing-select"
						value={billingType}
						onChange={(e) => {
							const value = e.target.value;
							dispatch(setBillingType(value));
							const params = new URLSearchParams(searchParams);
							params.set('billing', value);
							params.set('step', 'billing-details');
							if (selectedPlan?.key) {
								params.set('plan', selectedPlan.key);
							}
							setSearchParams(params);
							if (appliedCoupon) {
								toast.info('Pricing updated based on billing period');
							}
						}}
					>
						<option value="monthly">Monthly</option>
						<option value="yearly">Yearly</option>
					</select>
				</div>
			</div>
			<div className="mt-3">
				<p className="coupon-label">Have a coupon code?</p>

				<div className="d-flex gap-2">
					<div className="flex-grow-1 position-relative">
						<InputField
							value={coupon}
							onChange={setCoupon}
							placeholder="Enter coupon"
							disabled={!!appliedCoupon}
							className="form-control-coupon"
						/>

						{appliedCoupon && <FaCheck className="coupon-check" />}
					</div>

					<button
						type="button"
						className="apply-btn"
						onClick={handleApplyCoupon}
						disabled={!!appliedCoupon}
					>
						Apply
					</button>
				</div>

				{appliedCoupon && (
					<div className="coupon-success-text d-flex justify-content-between">
						<span>Coupon applied successfully</span>
						<span
							className="remove"
							onClick={handleRemoveCoupon}
						>
							Remove
						</span>
					</div>
				)}
			</div>
			<div className="mt-4 d-flex align-items-start gap-3 text-start">
				<div className="d-flex justify-content-center mt-1">
					<img
						src={lineImg}
						alt="timeline"
						style={{ height: '40px' }}
					/>
				</div>

				<div className="d-flex flex-column gap-3 small">
					<div>
						<strong>Today: </strong>
						{subscriptionDates.today} -
						{billingType === 'monthly'
							? `Monthly Subscription for ₹${planAmount.toFixed(2)} + GST ₹${gstAmount.toFixed(2)}`
							: `Yearly Subscription for ₹${planAmount.toFixed(2)} + GST ₹${gstAmount.toFixed(2)}`}
					</div>

					<div>
						<strong>Till: </strong> {subscriptionDates.expiry}
					</div>
				</div>
			</div>
			<hr />
			<div className="price-row-checkout">
				<span>Sub Total</span>
				<span>₹{planAmount.toFixed(2)}</span>
			</div>

			{discount > 0 && (
				<div className="price-row-checkout text-success">
					<span>Discount ({appliedCoupon})</span>
					<span>-₹{discount.toFixed(2)}</span>
				</div>
			)}

			<div className="price-row-checkout">
				<span>Taxes ({activeBilling?.tax_percentage}%)</span>
				<span>₹{gstAmount.toFixed(2)}</span>
			</div>

			<hr />

			<div className="price-row-checkout total">
				<span>Grand Total</span>
				<span>₹{totalAmount.toFixed(2)}</span>
			</div>
			<div className="text-center mt-3">
				<button
					onClick={handlePayment}
					className={`btn checkout-payment-btn btn-primary px-4 ${loading ? 'disabled-btn' : ''}`}
				>
					{loading ? (
						<>
							<Loader2
								size={18}
								strokeWidth={3}
								className="checkout-loader"
							/>

							<span>Processing...</span>
						</>
					) : (
						'Proceed To Payment'
					)}
				</button>
			</div>
			<div className="secure mt-3">
				<div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
					<div className="d-flex align-items-center gap-2">
						<img
							className="payment-icons-lock"
							src={lockImg}
							alt="lock"
						/>
						<span className="small">Secure Payment</span>
					</div>

					<div className="d-flex align-items-center gap-2">
						<img
							src={visaImg}
							alt="visa"
							className="payment-icon"
						/>
						<img
							src={rupayImg}
							alt="rupay"
							className="payment-icon"
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
